import Header from "../../ui/header/Header"
import React, { FC, useEffect, useState } from "react"
import { Announcement, MasterTemplateProps } from "../../../types"
import Navigation from "../navigation/Navigation"
import { useLocation, useNavigate } from "react-router-dom"
import "../../styles/App.css"
import ChatAssistant from "../chat-assistant/ChatAssistant"
import AdminNavigation from "../navigation/admin/AdminNavigation.tsx"
import { announcements } from "../../../apis/index.ts"
import { Dialog } from "@headlessui/react"
import { KButton, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { lang } from "../../../constants/languages/index.tsx"
import { toast } from "react-toastify"
import { errorFilledToastOptions } from "../../../constants/index.tsx"
import UnreadNotificationModal from "../notifications/UnreadNotificationModal.tsx"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

const MasterTemplate: FC<MasterTemplateProps> = (props) => {
  const location = useLocation()
  const path = location.pathname
  const segments = path.split("/").filter(Boolean)
  const [announcementList, setAnnouncementList] = useState<Announcement[]>([])

  const navigate = useNavigate()

  useEffect(() => {
    if (localStorage.getItem("releaseSubmission") === "1" && !checkPublicPage()) {
      localStorage.removeItem("releaseSubmission")
      navigate(`/catalog/release-submission`)
    }

    if (!checkPublicPage()) {
      announcements("/", "get").then((resp) => {
        if (resp.data.results) {
          const now = new Date()
          // Filter announcements based on startDate - only show if startDate has passed or is not set
          const filteredAnnouncements = resp.data.results.filter((announcement: Announcement) => {
            if (!announcement.start_date) return true
            const startDate = new Date(announcement.start_date)
            return startDate <= now
          })
          setAnnouncementList(filteredAnnouncements)
        }
      })
    }
  }, [])

  const checkPublicPage = () => {
    return (
      path.includes("login") ||
      path.includes("forgot-password") ||
      path.includes("create-password") ||
      path.includes("invitation") ||
      path.includes("artist/") ||
      path.includes("smart-link/") ||
      path.includes("start-your-journey")
    )
  }

  const checkFullPage = () => {
    return (
      path.includes("login") ||
      path.includes("forgot-password") ||
      path.includes("create-password") ||
      path.includes("asset-registry") ||
      path.includes("release-registry") ||
      path.includes("smart-link") ||
      path.includes("invitation") ||
      path.includes("artist-space") ||
      path.includes("artist") ||
      (path.includes("playlists") && !path.includes("admin")) ||
      path.includes("catalog/new-release") ||
      path.includes("start-your-journey") ||
      path.includes("catalog/release-submission")
    )
  }

  const checkHomePage = () => {
    return (
      !path.includes("account") &&
      !path.includes("notifications") &&
      (!path.includes("catalog/") || (path.includes("catalog") && segments.length === 1))
    )
  }

  const checkAdminPage = () => {
    return path.includes("admin") && !(path.includes("catalog") && segments.length === 3)
  }

  const renderContainer = () => {
    const width = checkFullPage() || checkAdminPage() ? "100%" : checkHomePage() ? "80%" : "68%"

    const margin = width === "80%" ? 72 : 0

    return (
      <div
        style={{
          marginLeft: margin,
          marginRight: margin,
          width,
          overflowY: "scroll"
        }}
      >
        {props.children}
      </div>
    )
  }

  const renderNavigation = () => {
    const width = checkAdminPage() ? "45px" : checkHomePage() ? "20%" : "16%"
    return (
      <div style={{ width, height: checkAdminPage() ? "100vh" : "calc(100vh - 64px)" }}>
        {checkAdminPage() && <AdminNavigation />}
        {checkHomePage() && !checkAdminPage() && <Navigation />}
      </div>
    )
  }

  const readAnnouncement = (announcementList: Announcement[]) => {
    announcements("/read/", "post", { id_list: announcementList.map((announcement) => announcement.id) }).then(
      (resp) => {
        if (resp.data.success) {
          setAnnouncementList([])
        } else {
          toast.error(lang.announcements.error, errorFilledToastOptions)
        }
      }
    )
  }

  const renderAnnouncementModal = () => {
    return (
      <Dialog open={announcementList.length > 0} onClose={() => setAnnouncementList([])}>
        <div
          className="fixed w-screen h-screen top-0 left-0 flex items-center justify-center bg-[#0000004d] "
          style={{ zIndex: 9999 }}
        >
          <div className="p-6 shadow-md rounded-[10px] bg-[#fff]">
            <KTitleSpan text={lang.announcements.title} lineHeight="22px" fontSize={20} />
            <div className="mt-4" style={{ maxHeight: 515, overflowY: "scroll" }}>
              {announcementList.map((announcement) => {
                const key = `description_${lang.common.lang_code}` as keyof Announcement
                const description: string = String(announcement?.[key] ?? "")
                return (
                  <div
                    className="pt-3 pb-3 flex-wrap"
                    key={`announcement_${announcement.id}`}
                    style={{
                      borderBottom: "1px dashed grey",
                      maxWidth: 900,
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      overflowWrap: "break-word"
                    }}
                  >
                    {description.split("\n").map((line, lineIndex) => (
                      <div
                        key={`line_${announcement.id}_${lineIndex}`}
                        style={{ marginBottom: "8px" }} // spacing between paragraphs
                      >
                        {line.split(/(<b>.*?<\/b>|https?:\/\/[^\s]+|\s+)/).map((chunk, index) => {
                          if (!chunk.trim()) return " " // keep spaces

                          const isUrl = /^https?:\/\//.test(chunk)
                          if (isUrl) {
                            return (
                              <a
                                key={`chunk_${announcement.id}_${lineIndex}_${index}`}
                                href={chunk}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontFamily: "Inter",
                                  fontSize: 15,
                                  color: "#1a73e8",
                                  textDecoration: "underline"
                                }}
                              >
                                {chunk}
                              </a>
                            )
                          }

                          const isBold = chunk.startsWith("<b>")
                          const text = chunk.replace(/<\/?b>/g, "")

                          return (
                            <span
                              key={`chunk_${announcement.id}_${lineIndex}_${index}`}
                              style={{
                                fontWeight: isBold ? "bold" : "normal",
                                fontFamily: "Inter",
                                fontSize: 15
                              }}
                            >
                              {text}
                            </span>
                          )
                        })}
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
            <div className="flex justify-end pt-4" style={{ borderTop: "1px dashed grey" }}>
              <KButton
                width="auto"
                background="black"
                textColor="white"
                text={lang.announcements.read}
                onClick={() => readAnnouncement(announcementList)}
              />
            </div>
          </div>
        </div>
      </Dialog>
    )
  }

  return (
    <>
      {announcementList.length > 0 && renderAnnouncementModal()}
      {!(announcementList.length > 0) && !checkPublicPage() && <UnreadNotificationModal />}
      {!checkFullPage() && !checkAdminPage() && <Header />}
      <div className={"flex"} style={{ width: "100%", overflow: "hidden" }}>
        {!checkFullPage() && renderNavigation()}
        {renderContainer()}
        {
          //!checkPublicPage() && <ChatAssistant />
        }
      </div>
    </>
  )
}

export default MasterTemplate
